import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProjectSettings,
  updateProjectSettings,
  SettingsConflictError,
} from '../../lib/api/projects';
import type { ProjectSettingsView, ProjectSettings } from '../../../../shared/types/projects';
import { validateAuthConfig } from '../../../../shared/lib/projectValidation';

/**
 * FORGE-178 Phase 3 — repo Access tab. Lets a project choose how much access
 * Forge gets: the all-in-one GitHub App (unified, default) or separate
 * least-privilege credentials per capability (granular). Friendly labelled
 * fields map onto the `auth` block the orchestrator reads from the project row;
 * the same `validateAuthConfig` the backend enforces runs inline so a config
 * that saves is one the orchestrator accepts. Complementary: leaving it on
 * "Unified" is exactly today's behavior.
 */

type Mode = 'unified' | 'granular';
interface Slot { type: string; secret_arn?: string }
interface AuthDraft { mode: Mode; git?: Slot; collab?: Slot; ci_obs?: Slot; events?: Slot }

const SLOT_TYPES = {
  git:    [['app', 'Shared GitHub App'], ['fine_pat', 'Fine-grained PAT'], ['deploy_key', 'Deploy key (SSH)']],
  collab: [['app', 'Shared GitHub App'], ['fine_pat', 'Fine-grained PAT']],
  ci_obs: [['app', 'Shared GitHub App'], ['fine_pat', 'Fine-grained PAT'], ['off', 'Disabled']],
  events: [['app_webhook', 'App webhook'], ['repo_webhook', 'Repo webhook'], ['poll', 'Polling']],
} as const;

const SLOT_META: Record<keyof typeof SLOT_TYPES, { label: string; help: string }> = {
  git:    { label: 'Code (clone / push)', help: 'How agents read and write repository code. A deploy key is the most isolated (per-repo, no API identity); a fine-grained PAT scoped to Contents works across repos.' },
  collab: { label: 'Collaboration (issues, PRs, comments, merges)', help: 'The token used for the GitHub API. A fine-grained PAT scoped to Issues + Pull requests keeps it separate from the code-write credential.' },
  ci_obs: { label: 'CI logs (read Actions failure logs)', help: 'Lets devops-fix read failed workflow logs. "Disabled" still works — it falls back to the deploy-result comment.' },
  events: { label: 'Events (webhooks)', help: 'How Forge hears about issue/label activity. "Polling" needs no inbound webhook (good behind a firewall).' },
};

/** Types that require a Secrets Manager ARN. */
const NEEDS_ARN = new Set(['fine_pat', 'deploy_key', 'repo_webhook']);

const readAuth = (s: ProjectSettings): AuthDraft => {
  const a = (s as any).auth;
  if (!a || typeof a !== 'object' || String(a.mode).toLowerCase() !== 'granular') return { mode: 'unified' };
  const slot = (v: any): Slot | undefined =>
    v && typeof v === 'object' ? { type: String(v.type ?? 'app'), secret_arn: v.secret_arn } : undefined;
  return { mode: 'granular', git: slot(a.git), collab: slot(a.collab), ci_obs: slot(a.ci_obs), events: slot(a.events) };
};

/** Strip the draft into the persisted `auth` shape (omit empty slots / arns). */
const toAuthConfig = (d: AuthDraft): Record<string, unknown> | undefined => {
  if (d.mode !== 'granular') return { mode: 'unified' };
  const out: Record<string, unknown> = { mode: 'granular' };
  (['git', 'collab', 'ci_obs', 'events'] as const).forEach((k) => {
    const slot = d[k];
    if (slot && slot.type) {
      const entry: Slot = { type: slot.type };
      if (slot.secret_arn?.trim()) entry.secret_arn = slot.secret_arn.trim();
      out[k] = entry;
    }
  });
  return out;
};

export const AccessTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const settingsQ = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  if (settingsQ.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (settingsQ.isError)
    return <div role="alert" className="text-sm text-red-600">{(settingsQ.error as Error).message}</div>;
  if (!settingsQ.data) return null;
  return <AccessEditor projectId={projectId} initial={settingsQ.data} onSaved={() => settingsQ.refetch()} />;
};

const AccessEditor: React.FC<{
  projectId: string;
  initial: ProjectSettingsView;
  onSaved: () => void;
}> = ({ projectId, initial, onSaved }) => {
  const [view, setView] = useState(initial);
  const [draft, setDraft] = useState<AuthDraft>(() => readAuth(initial.settings));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);

  const authConfig = useMemo(() => toAuthConfig(draft), [draft]);
  const errors = useMemo(() => validateAuthConfig(authConfig), [authConfig]);
  const isDirty = JSON.stringify(authConfig) !== JSON.stringify((view.settings as any).auth ?? { mode: 'unified' });

  const setSlot = (k: keyof typeof SLOT_TYPES, patch: Partial<Slot>) =>
    setDraft((d) => ({ ...d, [k]: { type: 'app', ...(d[k] ?? {}), ...patch } as Slot }));

  const handleSave = async () => {
    setSaveError(null); setConflictMsg(null);
    if (errors.length > 0) { setSaveError(errors[0]); return; }
    setSaving(true);
    try {
      const nextSettings = { ...view.settings, auth: authConfig } as ProjectSettings;
      const newView = await updateProjectSettings(projectId, {
        settings: nextSettings,
        expectedVersion: view.version === 0 ? null : view.version,
      });
      setView(newView);
      setDraft(readAuth(newView.settings));
      onSaved();
    } catch (err) {
      if (err instanceof SettingsConflictError) { setConflictMsg(err.message); setView(err.current); }
      else setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="access-tab">
      <p className="text-sm text-slate-600">
        Choose how much access Forge gets to this project's repos. <strong>Unified</strong> uses one GitHub
        App for everything (simplest). <strong>Granular</strong> lets you supply a separate least-privilege
        credential per capability, so no single credential can do everything.
      </p>

      {/* Mode selector */}
      <div className="grid grid-cols-2 gap-3">
        {(['unified', 'granular'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`access-mode-${m}`}
            onClick={() => setDraft((d) => ({ ...d, mode: m }))}
            className={`text-left rounded-lg border px-4 py-3 transition ${
              draft.mode === m ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className="text-sm font-semibold text-slate-800 capitalize">{m}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {m === 'unified' ? 'One GitHub App (default)' : 'Separate credential per capability'}
            </div>
          </button>
        ))}
      </div>

      {/* Granular slots */}
      {draft.mode === 'granular' && (
        <div className="space-y-4">
          {(Object.keys(SLOT_TYPES) as Array<keyof typeof SLOT_TYPES>).map((k) => {
            const slot = draft[k] ?? { type: SLOT_TYPES[k][0][0] };
            const needsArn = NEEDS_ARN.has(slot.type);
            return (
              <div key={k} className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid={`access-slot-${k}`}>
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-slate-800">{SLOT_META[k].label}</label>
                  <select
                    data-testid={`access-${k}-type`}
                    className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white"
                    value={slot.type}
                    onChange={(e) => setSlot(k, { type: e.target.value })}
                  >
                    {SLOT_TYPES[k].map(([val, lbl]) => (
                      <option key={val} value={val}>{lbl}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-slate-500">{SLOT_META[k].help}</p>
                {needsArn && (
                  <input
                    type="text"
                    data-testid={`access-${k}-arn`}
                    placeholder="arn:aws:secretsmanager:us-east-1:…:secret:/forge/…"
                    className="w-full text-sm font-mono border border-slate-300 rounded-md px-2 py-1"
                    value={slot.secret_arn ?? ''}
                    onChange={(e) => setSlot(k, { secret_arn: e.target.value })}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {errors.length > 0 && (
        <ul className="text-sm text-red-600 list-disc list-inside" data-testid="access-errors">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      {saveError && <div role="alert" className="text-sm text-red-600">{saveError}</div>}
      {conflictMsg && <div role="alert" className="text-sm text-amber-700">{conflictMsg}</div>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="access-save"
          disabled={!isDirty || saving || errors.length > 0}
          onClick={handleSave}
          className="text-sm font-medium px-4 py-2 rounded-md bg-indigo-600 text-white disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {isDirty && <span className="text-xs text-slate-500">Unsaved changes</span>}
      </div>
    </div>
  );
};
